/**
 * The public path the tracking script is served from — a FROZEN CONTRACT.
 *
 * ## What "frozen" means here, precisely
 *
 * This string ends up inside a `<script src>` in a customer's HTML, pasted once
 * and then forgotten. It is not deployed by us: it is deployed by every
 * customer, on their own schedule, and a good number of them will never touch
 * that tag again. So the set of paths that must keep answering is not "the
 * current one" — it is "every one we have ever published", for as long as any
 * page still carries it.
 *
 * The consequences, in order of how expensive they are to learn late:
 *
 *  1. **This value is never edited.** A breaking change to what the script
 *     sends becomes `/v2/…` served alongside `/v1/…`; it does not change under
 *     a tag somebody pasted a year ago. `/v1/` is the version of the WIRE
 *     CONTRACT, not of the build — a bug fix ships to the same path, which is
 *     the whole reason we host the file rather than asking customers to.
 *  2. **The path must never fall through to an HTML document.** nginx matches
 *     it exactly, ahead of the SPA and ahead of the marketing site, and serves
 *     it with a JavaScript content type and open CORS. Serving a customer's
 *     page an HTML document with a script's job is a real incident that has
 *     happened on this codebase before, and the surfaces suite asserts against
 *     it on the composed stack.
 *  3. **Renaming it later is not a refactor.** It is a migration that requires
 *     every customer to edit their site, so it is effectively impossible. Pick
 *     once, here, and treat the next `/v2/` as the only escape hatch.
 *
 * ## Why this filename
 *
 * Short, because the tag is in the critical path of somebody else's page load.
 * Neutral, because filter lists — uBlock Origin, EasyPrivacy, and the blocking
 * that browsers and DNS resolvers now do by default — match on the *name* as
 * much as on the host. A file called `analytics.js` or `tracking.js` is blocked
 * by pattern, and a blocked script is not an inconvenience on this product: it
 * is a silent, uneven undercount that makes every number wrong in a direction
 * nobody can see.
 *
 * That is a legitimate thing to optimise for HERE and nowhere else, and the
 * reason is worth writing down so it is not mistaken for evasion: Panel sets no
 * cookie, reads no storage, never writes an IP address and cannot join two days
 * of data to one person. There is no tracking to hide. The name is short
 * because the thing it names is small, and neutral because a pattern match on a
 * filename is not a judgement about behaviour.
 */
export const PUBLIC_SCRIPT_PATH = '/v1/p.js';

/**
 * The attribute the snippet carries the public key in.
 *
 * Frozen for the same reason as the path and with the same escape hatch: it is
 * read by a script running from a tag that was pasted once. `data-panel-key`
 * over a query string so that one cached file serves every customer.
 */
export const PUBLIC_SCRIPT_KEY_ATTRIBUTE = 'data-panel-key';

/**
 * Where the script POSTS what it measured — the other half of the frozen
 * contract, and frozen for exactly the same reason.
 *
 * The path is compiled into a file that runs from a `<script src>` somebody
 * pasted once. A visitor loading a cached `/v1/p.js` tomorrow will POST to
 * whatever string was in it when it was built, so this endpoint has to keep
 * answering for as long as any page still carries the tag. A breaking change
 * to the payload becomes `/api/v2/e` served alongside this one; it never
 * changes meaning under a script already in the wild.
 *
 * ## Three properties of this route that are not negotiable
 *
 *  1. **It answers 204 to everything it will ever answer.** Accepted, over
 *     budget, malformed, unknown key — all 204, identical bodies, identical
 *     headers. AGENTS.md already says this for the over-budget case and the
 *     reason generalises: a 400 or a 429 here reaches a script on a stranger's
 *     page, invites a retry that amplifies the loop the budget exists to stop,
 *     and tells an unauthenticated caller something about our internal state.
 *     The script does not read the response and cannot act on it.
 *  2. **The body arrives as `text/plain`, not as JSON.** `navigator.sendBeacon`
 *     is the only transport that survives a page unloading, and a `Blob` typed
 *     `application/json` makes the request non-simple: the browser sends a CORS
 *     preflight first, a preflight cannot complete on a closing document, and
 *     the last event of every session is silently lost. `text/plain` is a
 *     CORS-safelisted content type, so the POST goes straight out with no
 *     preflight at all. **Fastify does not parse that content type by default.**
 *     The ingest route has to register a parser for it, or every payload
 *     arrives as `undefined` while every test that posts `application/json`
 *     stays green — see `transport.ts` in `packages/tracker`.
 *  3. **`Access-Control-Allow-Origin: *`.** It is called from origins we do not
 *     own, by definition. No credentials travel in either direction: the script
 *     sets `credentials: 'omit'`, and there is nothing to send because Panel
 *     sets no cookie on any origin.
 */
export const PUBLIC_INGEST_PATH = '/api/v1/e';

/**
 * A measurement, as it travels.
 *
 * ## Why the keys are one letter
 *
 * This object is serialised on every page view of every page of every customer
 * site. `{"n":"pageview"}` against `{"name":"pageview"}` is not worth arguing
 * about once; multiplied by the traffic of everyone who installs Panel it is
 * bandwidth we impose on other people's visitors, over mobile connections we
 * do not pay for. The cost of the abbreviation is this one table, read once.
 *
 * The long-form vocabulary — what a device type is, what an event name may
 * contain, how long a path may be — belongs to `../analytics/index.js` and is
 * NOT restated here. That module is the storage schema's dictionary and this
 * one is the wire's; the wire is an abbreviation OF the dictionary, so the only
 * thing that lives here is the mapping between them ({@link
 * VIEWPORT_CLASS_TO_DEVICE}) and the one field the database has no opinion
 * about yet ({@link MAX_SCRIPT_HOSTS}).
 *
 * ## What is NOT in this type, and why that is the product
 *
 * No cookie id, no device id, no `localStorage` value, no fingerprint, no IP —
 * the visitor id is derived server-side from a salt that is destroyed nightly
 * and is never computed, seen or storable in the browser. No page title: a
 * title bar reading "Order #4471 — Jean Dupont" is the most common way a
 * person's name gets into analytics. No full referrer either, only its host.
 *
 * **No query string, with one closed exception**: the five names in
 * {@link UTM_QUERY_PARAMS}, read by name and sent in {@link TrackerEvent.c}.
 * Nothing else from the query survives the browser, and the exception is
 * argued where the reading happens (`packages/tracker/src/page.ts`) rather
 * than assumed here.
 *
 * Adding a field here that survives midnight is out of scope for Panel, not a
 * trade to be weighed. See `docs/BRIEF.md`.
 *
 * ## No zod schema in this module, deliberately
 *
 * This file is bundled verbatim into `/v1/p.js`, whose gzipped byte count is
 * enforced by a script. Importing a validator here would drag the validator
 * into a browser bundle that has nothing to validate — the script is the
 * producer, and the only consumer that must not trust it runs on the server.
 * The ingest route owns the schema; `../analytics/index.js` owns the clamps
 * both sides apply, so there is nothing left here to drift.
 */
export interface TrackerEvent {
  /** The public key, `pk_live_…`, read from the script tag. Public by design. */
  k: string;
  /** Event name; `PAGEVIEW_EVENT_NAME` from `../analytics/index.js` for a page view. */
  n: string;
  /** Path only. Scrubbed in the browser and again on arrival. Never a query, never a fragment. */
  u: string;
  /** Referrer HOST, absent when there was none or when it was the page's own. */
  r?: string;
  /** Viewport class — see {@link VIEWPORT_CLASS_TO_DEVICE}. Bucketed in the browser so the raw width never travels. */
  v?: ViewportClass;
  /**
   * The five campaign parameters, and nothing else from the query string.
   *
   * Absent when the URL carried none, which is the overwhelming majority of
   * page views. See {@link UTM_QUERY_PARAMS} for why these five names and no
   * others, and {@link UTM_WIRE_KEY_TO_COLUMN} for what each letter becomes.
   */
  c?: Partial<Record<UtmWireKey, string>>;
  /** Named-event properties. Scalars only; `sanitizeEventProps` is what decides on arrival. */
  p?: Record<string, string | number | boolean>;
  /**
   * Where an outbound click or a download went: a host with a scrubbed path
   * glued to it, `instagram.com/emnashop`. Absent on every other event.
   *
   * ## Why this is a field and not a property in `p`
   *
   * `p` is the CUSTOMER'S bag. Putting Panel's own measurement in it would mean
   * the rollup had to read a JSON column on every row of the widest table in
   * the product to find out where a click went — a column it is careful not to
   * read unless a revenue goal asks for it — and it would put a field the
   * manifest must disclose inside one whose disclosed purpose is "whatever the
   * site chose to measure". A field of our own is a column of our own, indexed
   * by nothing, read by the rollup for free alongside the path.
   *
   * ## Why a path comes with the host, when a referrer gets no path at all
   *
   * `r` is somebody ELSE'S URL, arrived at by a visitor whose business it is;
   * `mail.example/read?msg=…` is exactly what dropping the path protects. This
   * is a link the CUSTOMER published in their own template, on their own page,
   * identical for every visitor who sees it — the same standing as the path we
   * already send. So it keeps its path, and the path goes through the identical
   * `scrubPath` the page's own does: no query string, no fragment, identifier
   * segments collapsed to `:id`.
   *
   * `http` and `https` only. A `mailto:` link is refused rather than clamped,
   * because the interesting half of one is an address.
   */
  d?: string;
  /**
   * Hosts of the third-party scripts sharing the page, sent once per page load.
   *
   * This describes the CUSTOMER'S OWN PAGE, not their visitor: it is the same
   * list they would read in "view source" on their own site, it contains no
   * identifier, and it is identical for two people loading the same page.
   *
   * It exists because the first support question about a tracking script is
   * always "why are my numbers wrong", and the answer is almost never the
   * tracking script. It is a consent manager holding the tag until a click that
   * never comes, a tag manager that injected a second copy so everything counts
   * twice, or an A/B tool that reloads the page. None of that is visible from
   * the server, and all of it is visible from inside the page for free.
   *
   * Hosts and never URLs: a third-party script URL with an account id or a
   * session token in its query string is completely ordinary.
   */
  s?: string[];
  /**
   * `1` when `history.pushState` was ALREADY wrapped by somebody else before we
   * loaded.
   *
   * The highest-value diagnostic on this list and it costs one integer. Two
   * scripts wrapping the same method is the standard way a single-page app's
   * view count goes wrong — double counts when both fire, missing counts when
   * one forgets to call through — and it is invisible from the server,
   * invisible in the dashboard, and invisible to the customer, who sees only
   * numbers that disagree with their other tool.
   */
  x?: 1;
}

/** The three values `v` may take. */
export type ViewportClass = 'm' | 't' | 'd';

/**
 * The wire's abbreviation, expanded into the storage vocabulary.
 *
 * Both halves import this rather than writing the mapping out, because a
 * mapping written twice is a mapping that eventually disagrees — and the shape
 * of that disagreement is every tablet in the database quietly becoming a
 * desktop, which no test would notice and no customer could report precisely.
 * The values are the members of `DEVICE_TYPES` in `../analytics/index.js`.
 */
export const VIEWPORT_CLASS_TO_DEVICE = {
  m: 'mobile',
  t: 'tablet',
  d: 'desktop',
} as const satisfies Record<ViewportClass, 'mobile' | 'tablet' | 'desktop'>;

/**
 * The query parameters the script is allowed to read, and the letter each one
 * travels as.
 *
 * ## Why an allow-list of exactly five names is not "the query string"
 *
 * The rule this script is built on is that the query string does not leave the
 * page, because `?ref=newsletter` and `?token=…` are the same syntax and no
 * SHAPE tells them apart. That argument is about shape. It does not apply to a
 * NAME: these five are a twenty-year-old convention for campaign attribution,
 * they are written by the customer into links the customer publishes, and an
 * application does not keep its state in them. A closed list of five literals
 * cannot widen by accident the way a heuristic can.
 *
 * The list is a contract like the paths above and for the same reason — it is
 * compiled into a file somebody pasted once — so it is added to rather than
 * edited, and a sixth name would want the argument above to be made again for
 * it. There is no `gclid`, no `fbclid` and no `msclkid`: those are click
 * identifiers, unique per click, which is the persistent-identifier shape this
 * product refuses. See `docs/BRIEF.md`.
 *
 * Reading them is on by default and `data-panel-utm="false"` turns it off, for
 * the customer who decides that even these five are more than they want
 * leaving their pages.
 */
export const UTM_QUERY_PARAMS = {
  utm_source: 's',
  utm_medium: 'm',
  utm_campaign: 'c',
  utm_term: 't',
  // `o` and not `c`: `c` is already the container field's own name, and a
  // one-letter key that means two things in the same payload is how a wire
  // format acquires a bug nobody can read.
  utm_content: 'o',
} as const;

export type UtmWireKey = (typeof UTM_QUERY_PARAMS)[keyof typeof UTM_QUERY_PARAMS];

/**
 * The wire's letter, expanded into the column it is stored in.
 *
 * Here for the same reason as {@link VIEWPORT_CLASS_TO_DEVICE}: a mapping
 * written twice eventually disagrees, and the shape of THIS disagreement is
 * every campaign in the database filed under "medium" — which no test notices
 * and no customer can report precisely, because both columns are full of
 * plausible words.
 */
export const UTM_WIRE_KEY_TO_COLUMN = {
  s: 'utmSource',
  m: 'utmMedium',
  c: 'utmCampaign',
  t: 'utmTerm',
  o: 'utmContent',
} as const satisfies Record<UtmWireKey, string>;

/**
 * How many third-party script hosts one page load may report.
 *
 * A page with more than a dozen third-party scripts has a problem that this
 * list is not going to help anybody diagnose, and the cap is what stops a
 * pathological page — or somebody posting a handcrafted payload at a public
 * endpoint — from turning a diagnostic into an unbounded array.
 */
export const MAX_SCRIPT_HOSTS = 12;
